import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface MaintenanceModule {
    id: string;
    name: string;
    icon: React.ComponentType<any>;
    description: string;
    tableName: string;
    fields: string[];
}

interface SelectedRows {
    [key: string]: boolean;
}

interface MaintenanceSidePanelProps {
    module: MaintenanceModule;
    selectedRows: SelectedRows;
    onClose: () => void;
    onSave: () => void;
}

interface FieldValue {
    [key: string]: any;
}

interface BulkEditConfig {
    [key: string]: {
        enabled: boolean;
        value: any;
    };
}

export const MaintenanceSidePanel: React.FC<MaintenanceSidePanelProps> = ({
    module,
    selectedRows,
    onClose,
    onSave
}) => {
    const { t } = useTranslation();
    const [bulkEditConfig, setBulkEditConfig] = useState<BulkEditConfig>({});
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const selectedRowCount = Object.values(selectedRows).filter(Boolean).length;

    // Initialize bulk edit config when component mounts
    useEffect(() => {
        const initialConfig: BulkEditConfig = {};
        module.fields.forEach(field => {
            initialConfig[field] = {
                enabled: false,
                value: getDefaultValue(field)
            };
        });
        setBulkEditConfig(initialConfig);
    }, [module.fields]);

    const getDefaultValue = (field: string): any => {
        switch (field) {
            case 'ActiveYesNo':
                return 'Ja';
            case 'BinTypeNo':
            case 'BinGrpNo':
            case 'MovementTypeNo':
            case 'BCElementID':
            case 'BarcodeID':
            case 'PickPriorityNo':
            case 'PutAwayPriorityNo':
            case 'Priority':
            case 'FromPriority':
            case 'ToPriority':
            case 'Length':
                return 1;
            default:
                return '';
        }
    };

    const getFieldLabel = (field: string): string => {
        const labels: { [key: string]: string } = {
            'ItemNo': t('warehouse.general.itemNumber', {}, 'Varenummer'),
            'Description1': t('common.fields.description', {}, 'Beskrivelse'),
            'Description': t('common.fields.description', {}, 'Beskrivelse'),
            'UnitOfMeasure': t('manager.modules.adminResources.maintenanceFields.unitOfMeasure', {}, 'Enhet'),
            'ItemGroup': t('manager.modules.adminResources.maintenanceFields.itemGroup', {}, 'Varegruppe'),
            'BinCode': t('manager.modules.adminResources.maintenanceFields.binCode', {}, 'Lokasjonskode'),
            'WarehouseID': t('manager.modules.adminResources.maintenanceFields.warehouseId', {}, 'Lager-ID'),
            'StcZoneID': t('manager.modules.adminResources.maintenanceFields.zoneId', {}, 'Sone-ID'),
            'BinTypeNo': t('manager.modules.adminResources.maintenanceFields.binTypeNo', {}, 'Lokasjonstype nr.'),
            'BinGrpNo': t('manager.modules.adminResources.maintenanceFields.binGrpNo', {}, 'Lokasjonsgruppe nr.'),
            'MovementTypeNo': t('manager.modules.adminResources.maintenanceFields.movementTypeNo', {}, 'Flyttetype nr.'),
            'BCElementID': t('manager.modules.adminResources.maintenanceFields.bcElementId', {}, 'Strekkodeelement-ID'),
            'BarcodeID': t('manager.modules.adminResources.maintenanceFields.barcodeId', {}, 'Strekkode-ID'),
            'PickPriorityNo': t('manager.modules.adminResources.maintenanceFields.pickPriority', {}, 'Plukkprioritet'),
            'PutAwayPriorityNo': t('manager.modules.adminResources.maintenanceFields.putAwayPriority', {}, 'Plasseringsprioritet'),
            'Priority': t('common.fields.priority', {}, 'Prioritet'),
            'FromPriority': t('manager.modules.adminResources.maintenanceFields.fromPriority', {}, 'Fra prioritet'),
            'ToPriority': t('manager.modules.adminResources.maintenanceFields.toPriority', {}, 'Til prioritet'),
            'ActiveYesNo': t('manager.modules.adminResources.maintenanceFields.active', {}, 'Aktiv'),
            'AI': t('manager.modules.adminResources.maintenanceFields.aiCode', {}, 'AI-kode'),
            'Length': t('manager.modules.adminResources.maintenanceFields.length', {}, 'Lengde')
        };
        return labels[field] || field;
    };

    const getFieldInputType = (field: string): 'text' | 'number' | 'select' => {
        const numberFields = ['BinTypeNo', 'BinGrpNo', 'MovementTypeNo', 'BCElementID', 'BarcodeID', 
                             'PickPriorityNo', 'PutAwayPriorityNo', 'Priority', 'FromPriority', 'ToPriority', 'Length'];
        const selectFields = ['ActiveYesNo', 'UnitOfMeasure'];
        
        if (numberFields.includes(field)) return 'number';
        if (selectFields.includes(field)) return 'select';
        return 'text';
    };

    const getSelectOptions = (field: string): string[] => {
        switch (field) {
            case 'ActiveYesNo':
                return ['Ja', 'Nei'];
            case 'UnitOfMeasure':
                return ['STK', 'KG', 'L', 'M', 'M2', 'M3'];
            default:
                return [];
        }
    };

    const handleFieldToggle = (field: string, enabled: boolean) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                enabled
            }
        }));
    };

    const handleFieldValueChange = (field: string, value: any) => {
        setBulkEditConfig(prev => ({
            ...prev,
            [field]: {
                ...prev[field],
                value
            }
        }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const enabledFields = Object.entries(bulkEditConfig)
            .filter(([, config]) => config.enabled)
            .map(([field, config]) => ({ field, value: config.value }));

        console.log('Bulk updating fields:', enabledFields, 'for rows:', selectedRows);
        
        setIsSaving(false);
        setShowSuccess(true);
        
        setTimeout(() => {
            setShowSuccess(false);
            onSave();
        }, 1500);
    };

    const enabledFieldCount = Object.values(bulkEditConfig).filter(config => config.enabled).length;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black bg-opacity-25 transition-opacity" onClick={onClose} />
            
            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl border-l border-gray-200 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.adminResources.common.bulkUpdateHeading', {}, 'Masseoppdatering')}</h2>
                        <p className="text-sm text-gray-600">
                            {t('manager.modules.adminResources.maintenanceSidePanel.selectedInModule', { '0': selectedRowCount, '1': selectedRowCount === 1 ? t('manager.modules.adminResources.common.rowSingular', {}, 'rad') : t('manager.modules.adminResources.common.rowPlural', {}, 'rader'), '2': module.name }, `${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'} valgt i ${module.name}`)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-800">
                                <p className="font-medium mb-1">{t('manager.modules.adminResources.common.bulkUpdateHeading', {}, 'Masseoppdatering')}</p>
                                <p>{t('manager.modules.adminResources.common.bulkUpdateRowsInfo', {}, 'Velg feltene du ønsker å oppdatere for alle valgte rader. Kun markerte felt vil bli endret.')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Field Editor */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-medium text-gray-900">{t('manager.modules.adminResources.common.fieldsToUpdate', {}, 'Felter som skal oppdateres')}</h3>

                        {module.fields.map(field => {
                            const config = bulkEditConfig[field];
                            const inputType = getFieldInputType(field);
                            
                            if (!config) return null;

                            return (
                                <div 
                                    key={field} 
                                    className={`border rounded-lg p-3 transition-colors ${
                                        config.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                                    }`}
                                >
                                    {/* Field Toggle */}
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={config.enabled}
                                                onChange={(e) => handleFieldToggle(field, e.target.checked)}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-sm font-medium text-gray-700">
                                                {getFieldLabel(field)}
                                            </span>
                                        </label>
                                    </div>

                                    {/* Field Value Input */}
                                    {config.enabled && (
                                        <div className="mt-2">
                                            {inputType === 'select' ? (
                                                <select
                                                    value={config.value}
                                                    onChange={(e) => handleFieldValueChange(field, e.target.value)}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                >
                                                    {getSelectOptions(field).map(option => (
                                                        <option key={option} value={option}>
                                                            {option}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type={inputType}
                                                    value={config.value}
                                                    onChange={(e) => handleFieldValueChange(field, 
                                                        inputType === 'number' ? parseInt(e.target.value) || 0 : e.target.value
                                                    )}
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    placeholder={t('manager.modules.adminResources.common.newValueForPlaceholder', { '0': getFieldLabel(field) }, `Ny verdi for ${getFieldLabel(field)}`)}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Summary */}
                    {enabledFieldCount > 0 && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-green-800">
                                    <p className="font-medium">{t('manager.modules.adminResources.common.readyToSave', {}, 'Klar til å lagre')}</p>
                                    <p>
                                        {t('manager.modules.adminResources.common.fieldsWillUpdateRows', { '0': enabledFieldCount, '1': selectedRowCount, '2': selectedRowCount === 1 ? t('manager.modules.adminResources.common.rowSingular', {}, 'rad') : t('manager.modules.adminResources.common.rowPlural', {}, 'rader') }, `${enabledFieldCount} felt vil bli oppdatert for ${selectedRowCount} ${selectedRowCount === 1 ? 'rad' : 'rader'}`)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-center justify-between gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>

                        <button
                            onClick={handleSave}
                            disabled={enabledFieldCount === 0 || isSaving}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                enabledFieldCount === 0 || isSaving
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                        >
                            {isSaving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {t('common.status.saving', {}, 'Lagrer...')}
                                </>
                            ) : showSuccess ? (
                                <>
                                    <CheckCircle className="w-4 h-4" />
                                    {t('manager.modules.adminResources.common.saved', {}, 'Lagret!')}
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {t('manager.modules.adminResources.common.saveChanges', {}, 'Lagre endringer')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
